import type {ReactElement, ReactNode} from 'react';
import {useEffect, useRef, useState} from 'react';
import {GROWL_TYPE, hideMessageGrowl, removeMessageGrowl} from '@Components/message-growl';
import {Text, TextSize} from '@Components/text';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {MessageGrowlItem} from '@Components/message-growl/message-growl-reducer';
import styles from './snackbar-message-growl.module.scss';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';

interface SnackbarMessageGrowlItemProps {
  growl: MessageGrowlItem;
}

export function SnackbarMessageGrowlItem({growl}: SnackbarMessageGrowlItemProps): ReactElement | null {
  const currentLoading = useAppSelector((state) => {
    return state.loadingToast.currentLoading;
  });
  const [dummyState, setDummyState] = useState(false);
  const [closeEnded, setCloseEnded] = useState(false);
  const dispatch = useAppDispatch();
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!dummyState) {
      closeTimerRef.current = setTimeout((): void => {
        setCloseEnded(true);
      }, 500);

      return (): void => {
        if (closeTimerRef.current) {
          clearTimeout(closeTimerRef.current);
        }
      };
    }

    if (growl.sticky) {
      return undefined;
    }

    hideTimerRef.current = setTimeout(() => {
      setDummyState(false);
      dispatch(hideMessageGrowl(growl.key));
    }, growl.interval);

    return (): void => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [dispatch, dummyState, growl.key, growl.interval, growl.sticky]);

  useEffect(() => {
    if (!growl.show && closeEnded) {
      dispatch(removeMessageGrowl(growl.key));
    }
  }, [dispatch, growl.show, growl.key, closeEnded]);

  useEffect(() => {
    if (growl.show) {
      setCloseEnded(false);
      if (!currentLoading.show) {
        showTimerRef.current = setTimeout(() => {
          setDummyState(true);
        }, 500);
      }
    }

    return (): void => {
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
      }
    };
  }, [growl.show, currentLoading.show, growl.key]);

  useEffect(() => {
    if (!growl.show && dummyState) {
      setDummyState(false);
    }
  }, [growl.show, dummyState]);

  const getMessageGrowlClasses = (): string => {
    switch (growl.type) {
      case GROWL_TYPE.DANGER:
        return styles.danger;
      case GROWL_TYPE.PREMIUM:
        return styles.premium;
      case GROWL_TYPE.SECONDARY:
        return styles.secondary;
      case GROWL_TYPE.NEUTRAL:
        return styles.neutral;
      case GROWL_TYPE.SUCCESS:
      default:
        return styles.success;
    }
  };

  const isShowing = (): boolean => {
    return growl.show && dummyState;
  };

  const getIcon = (): ReactElement | null => {
    if (growl.type === GROWL_TYPE.DANGER) {
      return <Icon icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_16} className="content-body-white" />;
    }

    if (growl.icon) {
      return <Icon icon={growl.icon} size={IconSize.SIZE_ICON_16} className={growl.iconClassName ?? 'content-body-white'} />;
    }

    return null;
  };

  const isIconShowing = (): boolean => {
    return growl.type === GROWL_TYPE.DANGER || !!growl.icon;
  };

  const hasProgress = growl.progress !== undefined;

  const getCircularLoader = (): ReactNode => {
    if (!hasProgress && !growl.isLoading) {
      return null;
    }

    return <CircularProgressLoader size={LOADER_SIZE.XSMALL} color={LOADER_COLOR.WHITE} />;
  };

  const getActionLink = (): ReactElement | null => {
    if (!growl.action) {
      return null;
    }

    return (
      <a
        className={`${styles.actionLink} content-body-white spacing-m-l-1`}
        href={growl.action.url}
        target={growl.action.openInNewTab ? '_blank' : undefined}
        rel={growl.action.openInNewTab ? 'noopener noreferrer' : undefined}
      >
        {growl.action.label}
      </a>
    );
  };

  const getPercentageText = (): ReactNode => {
    if (!hasProgress) {
      return null;
    }

    const progressValue = Math.round(growl.progress ?? 0);

    return <Text className={`content-body-white spacing-m-l-2`} size={TextSize.XXSMALL} val={`(${progressValue}%)`} />;
  };

  return (
    <div
      className={`${styles.snackbar} flexbox flex-items-center ${getMessageGrowlClasses()} ${isShowing() ? styles.in : styles.out} flex-h-row radius-round ${
        !isShowing() && closeEnded ? styles.outEnded : ''
      }`}
    >
      {getCircularLoader()}
      {getIcon()}
      <Text
        dangerouslySetInnerHTML={growl.dangerouslySetInnerHTML}
        val={growl.text}
        className={`content-body-white ${isIconShowing() || hasProgress || growl.isLoading ? styles.marginLeft : ''}`}
        size={TextSize.XXSMALL}
      />
      {getActionLink()}
      {getPercentageText()}
      {growl.showCloseIcon ? (
        <Icon
          icon="icon-close"
          className="content-body-white spacing-m-l-4"
          size={IconSize.SIZE_ICON_16}
          onClick={(): void => {
            dispatch(hideMessageGrowl(growl.key));
          }}
        />
      ) : null}
    </div>
  );
}
